/**
 * 一括再配信モーダルの純関数 (文言・エラー分類・送信内容の組み立て).
 * 文言の正本は docs/design-docs/deliveries/bulk-resend.md §10.3.
 */
import type {
  BulkResendEligibility,
  BulkResendErrorCode,
  BulkResendItem,
  BulkResendPreviewItem,
  BulkResendResult,
} from "hooks/use-delivery-series-api";
import { RestApiError } from "lib/rest-client";

/** シリーズごとの件名・本文の編集値 */
export type BulkResendDraft = {
  subject: string;
  bodyText: string;
};

/** 実行 API の失敗の分類 (md §11.3) */
export type BulkResendFailure =
  | { kind: "quota"; remaining: number }
  | { kind: "rejected" }
  | { kind: "unknown" };

/**
 * 2 つ以上のシリーズに含まれる宛先があるときの注意.
 * @param {number} count 2 通以上届く人数 (preview の duplicatedRecipients)
 * @return {string} 文言
 */
export function duplicatedRecipientsNotice(count: number): string {
  return `⚠ ${count.toLocaleString()} 名に 2 通以上届きます（同じ配信グループを使うシリーズがあるため）`;
}

export const PREVIEW_FAILED_MESSAGE = "配信の確認内容を取得できませんでした";
export const REJECTED_MESSAGE =
  "配信できませんでした。1 件も送信していません。";
export const UNKNOWN_TITLE = "結果を確認できませんでした";
export const UNKNOWN_MESSAGE =
  "配信が始まっている可能性があります。同じ配信を二重に送らないよう、配信一覧で確認してください。";
export const UNCONFIRMED_NOTE = "配信一覧で確かめてから選び直してください。";
const UNCONFIRMED_REASON =
  "配信の開始を確認できませんでした。配信一覧で状況を確認してください。";
const SERIES_NOT_FOUND_MESSAGE = "シリーズが見つかりません。";
const SERIES_ARCHIVED_MESSAGE =
  "アーカイブ済みシリーズには新規配信できません。";
const TEMPLATE_GROUP_DELETED_MESSAGE = "雛型の配信グループが削除されています。";
const SENDER_UNVERIFIED_MESSAGE =
  "送信者のメールアドレスが認証されていません。";
const NO_RECIPIENTS_MESSAGE = "有効な宛先がありません。";
/** eligibility が ok なのに雛型が無い (契約違反) ときの表示 */
export const TEMPLATE_UNAVAILABLE_MESSAGE = "雛型を取得できませんでした。";

/**
 * 変数に代入して網羅を型で確かめる (md §11.4).
 * 到達したら例外を投げる (戻らない).
 * @param {never} value 到達しないはずの値
 */
function assertNever(value: never): never {
  throw new Error(`Unexpected value: ${String(value)}`);
}

/**
 * preview の送れない理由の文言. 送れるときは null.
 * @param {BulkResendEligibility} eligibility preview の eligibility
 * @return {string|null} 理由
 */
export function eligibilityReason(
  eligibility: BulkResendEligibility,
): string | null {
  switch (eligibility) {
    case "ok":
      return null;
    case "not_found":
      return SERIES_NOT_FOUND_MESSAGE;
    case "archived":
      return SERIES_ARCHIVED_MESSAGE;
    case "no_template":
      return "雛型にできる送信済みの配信がありません。";
    case "template_group_deleted":
      return TEMPLATE_GROUP_DELETED_MESSAGE;
    case "sender_unverified":
      return SENDER_UNVERIFIED_MESSAGE;
    case "no_recipients":
      return NO_RECIPIENTS_MESSAGE;
    default:
      return assertNever(eligibility);
  }
}

/**
 * 実行結果の失敗理由の文言.
 * @param {BulkResendErrorCode} code 実行結果の errorCode
 * @return {string} 理由
 */
export function errorCodeReason(code: BulkResendErrorCode): string {
  switch (code) {
    case "SERIES_NOT_FOUND":
      return SERIES_NOT_FOUND_MESSAGE;
    case "SERIES_ARCHIVED":
      return SERIES_ARCHIVED_MESSAGE;
    case "INVALID_TEMPLATE":
      return "雛型の配信が見つかりません。";
    case "TEMPLATE_GROUP_DELETED":
      return TEMPLATE_GROUP_DELETED_MESSAGE;
    case "SENDER_UNVERIFIED":
      return SENDER_UNVERIFIED_MESSAGE;
    case "NO_RECIPIENTS":
      return NO_RECIPIENTS_MESSAGE;
    case "START_FAILED":
      return "配信を開始できませんでした。";
    default:
      return assertNever(code);
  }
}

/**
 * 429 の文言. remaining が 0 なら上限に達した旨だけを返す.
 * @param {number} remaining API の remaining
 * @return {string} 文言
 */
export function quotaMessage(remaining: number): string {
  if (remaining <= 0) return "今の請求期間の上限に達しています。";
  return `上限まで残り ${remaining} 件のため送信していません。モーダルを閉じ、一覧で選ぶシリーズを ${remaining} 件以下にしてください（編集内容は失われます）。`;
}

/**
 * 実行 API の失敗を 3 通りに分ける.
 * - 429 かつ body.remaining が数値 → quota
 * - それ以外の 4xx (remaining の無い 429、401 を含む) → rejected (何も作っていない)
 * - 通信断・5xx・その他 → unknown (作られたか分からない)
 * @param {unknown} error 投げられた値
 * @return {BulkResendFailure} 分類
 */
export function classifyBulkResendError(error: unknown): BulkResendFailure {
  if (!(error instanceof RestApiError)) return { kind: "unknown" };
  if (error.status === 429) {
    const remaining =
      typeof error.body === "object" && error.body !== null
        ? (error.body as { remaining?: unknown }).remaining
        : undefined;
    if (typeof remaining === "number") return { kind: "quota", remaining };
  }
  if (error.status >= 400 && error.status < 500) return { kind: "rejected" };
  return { kind: "unknown" };
}

/**
 * 雛型と比べて編集されているか (trim 後の値で比べる).
 * @param {string} value 編集値
 * @param {string} original 雛型の値
 * @return {boolean} 編集されていれば true
 */
export function isChanged(value: string, original: string): boolean {
  return value.trim() !== original.trim();
}

/**
 * 送れるシリーズだけをリクエストの形にする. 雛型と違う項目だけ trim して入れる.
 * @param {BulkResendPreviewItem[]} items preview の items
 * @param {Record<string, BulkResendDraft>} drafts シリーズ ID ごとの編集値
 * @return {BulkResendItem[]} リクエストの items
 */
export function buildResendItems(
  items: BulkResendPreviewItem[],
  drafts: Record<string, BulkResendDraft>,
): BulkResendItem[] {
  return items.flatMap(item => {
    if (item.eligibility !== "ok" || !item.template) return [];
    const draft = drafts[item.seriesId];
    const { template } = item;
    return [
      {
        seriesId: item.seriesId,
        templateDeliveryId: template.deliveryId,
        ...(draft && isChanged(draft.subject, template.subject)
          ? { subject: draft.subject.trim() }
          : {}),
        ...(draft && isChanged(draft.bodyText, template.bodyText)
          ? { bodyText: draft.bodyText.trim() }
          : {}),
      },
    ];
  });
}

/**
 * 結果 1 件の表示 (アイコンの種類と理由).
 * @param {BulkResendResult} result 実行結果 1 件
 * @return {object} kind と reason (started のときは null)
 */
export function describeResult(result: BulkResendResult): {
  kind: "ok" | "ng" | "hold";
  reason: string | null;
} {
  switch (result.status) {
    case "started":
      return { kind: "ok", reason: null };
    case "failed":
      return {
        kind: "ng",
        reason: errorCodeReason(result.errorCode ?? "START_FAILED"),
      };
    case "unconfirmed":
      return { kind: "hold", reason: UNCONFIRMED_REASON };
    default:
      return assertNever(result.status);
  }
}

/**
 * 結果の見出し. 0 件の部分は省く. すべて開始なら「N 件の配信を開始しました」.
 * @param {BulkResendResult[]} results 実行結果
 * @return {string} 見出し
 */
export function resultHeadline(results: BulkResendResult[]): string {
  const started = results.filter(r => r.status === "started").length;
  const failed = results.filter(r => r.status === "failed").length;
  const unconfirmed = results.filter(r => r.status === "unconfirmed").length;
  if (failed === 0 && unconfirmed === 0) {
    return `${started} 件の配信を開始しました`;
  }
  const parts: string[] = [];
  if (started > 0) parts.push(`${started} 件を開始`);
  if (failed > 0) {
    parts.push(
      unconfirmed > 0
        ? `${failed} 件は開始できず`
        : `${failed} 件は開始できませんでした`,
    );
  }
  if (unconfirmed > 0) {
    parts.push(`${unconfirmed} 件は開始を確認できませんでした`);
  }
  return parts.join("、");
}
